import React, { useMemo } from 'react';

import { Footer, Icon } from '@bumble/ui-kit';
import { footer, common, header } from 'components/lexemes';
import { SUPPORT_URL } from 'lib/constants';
import { Country, isEuCountry } from 'lib/enums/country';
import { useGlobalProps } from 'lib/hooks/use-global-props';
import interfaceLanguages from 'lib/languages.json';
import LocaleContext from 'lib/locale-context';
import { getLocaleFromParams } from 'lib/localization';
import { useRouter } from 'next/router';

export type FooterContainerProps = {
  hideLanguageSelector?: boolean;
};

export const FooterContainer: React.FC<FooterContainerProps> = ({
  hideLanguageSelector: _hideLanguageSelector,
}) => {
  const lang = React.useContext(LocaleContext);
  const globalProps = useGlobalProps();
  const country = globalProps?.country;
  const router = useRouter();
  const languagesList = useMemo(() => {
    return interfaceLanguages.map(({ name, code }) => ({
      title: name,
      value: code,
      href: `/${code}`,
    }));
  }, []);

  const handlePathChange = (path: string, language: string) => {
    const locale = getLocaleFromParams({ lang: language });
    if (!locale) {
      return;
    }

    /**
     * Corner case for not found pages
     */
    if (router.pathname === '/[lang]/[...slug]') {
      return (window.location.href = '/');
    }

    /**
     * Route for feature pages
     */
    if (router.pathname.startsWith('/[lang]')) {
      return (window.location.href = router.pathname.replace('/[lang]', path));
    }

    const search = window.location.search === '' ? '' : window.location.search;
    const windowPathName = path + router.pathname + search;

    // @TODO - if we use basePath in next.config.js - use it
    // : window.location.pathname.replace(pathname, path);
    return (window.location.href = windowPathName);
  };

  return (
    <Footer
      appLinks={{
        title: footer.lxFooterAppsTitle(),
        links: [
          {
            text: footer.lxFooterItemBumbleDate(),
            url: `/${lang}/date`,
          },
          { text: 'BFF', url: `/${lang}/bff` },
          { text: 'Badoo', url: 'https://badoo.com', hasIcon: true },
        ],
      }}
      companyLinks={{
        title: footer.lxFooterCompanyTitle(),
        links: [
          { text: footer.lxFooterItemAbout(), url: `/${lang}/about` },
          {
            text: footer.lxFooterItemContactUs(),
            url: 'https://support.bumble.com/hc/articles/30933835506589',
          },
          {
            text: footer.lxFooterItemCareers(),
            url: 'https://team.bumble.com',
            hasIcon: true,
          },
          {
            text: footer.lxFooterItemInvestors(),
            url: 'https://ir.bumble.com',
            hasIcon: true,
          },
          {
            text: footer.lxFooterItemShop(),
            url: 'https://bumble.shop',
            hasIcon: true,
          },
          {
            text: header.lxHeaderItemLabelBuzz,
            url: '/the-buzz',
            hasIcon: true,
          },
          { text: footer.lxFooterItemSupport(), url: SUPPORT_URL },
        ],
      }}
      legalLinks={{
        title: footer.lxFooterLegalTitle(),
        links: [
          { text: footer.lxFooterItemGuidelines(), url: `/${lang}/guidelines` },
          country === Country.USA
            ? {
                text: footer.lxDoNotSell(),
                onClick: (event: React.MouseEvent<HTMLAnchorElement>) => {
                  event.preventDefault();

                  // CCPA Privacy Manager Call
                  try {
                    //@ts-ignore - describe the _sp_ object later
                    window._sp_.ccpa.loadPrivacyManagerModal('708248');
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  } catch (error) {
                    window.location.href = '/cookie-policy';
                  }
                },
              }
            : {
                text: footer.lxFooterItemManageCookies(),
                url: '/',
                onClick: (event: React.MouseEvent<HTMLAnchorElement>) => {
                  event.preventDefault();
                  // GDPR Privacy Manager Call - later list only GDPR countries
                  try {
                    // @ts-ignore - describe the _sp_ object later
                    window._sp_.gdpr.loadPrivacyManagerModal();
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  } catch (error) {
                    window.location.href = '/cookie-policy';
                  }
                },
              },
          country === Country.GERMANY
            ? {
                text: footer.lxGermanCancelationPolicyFlow(),
                url: `/${lang}/help/cancelation-flow`,
              }
            : null,
          {
            text: footer.lxFooterItemPrivacyPolicy(),
            url: lang ? `/privacy-policy/${lang}` : '/privacy-policy',
          },
          { text: footer.lxFooterItemTerms(), url: `/${lang}/terms` },
          {
            text: footer.lxFooterItemA11y(),
            url: isEuCountry(country)
              ? `/${lang}/eaa-accessibility-statement`
              : `/${lang}/accessibility`,
          },
          { text: footer.lxFooterItemNoticeAtCollection(), url: '/notice-at-collection' },
          {
            text: footer.lxFooterItemModernSlaveryAct(),
            url: '/modern-slavery-act-statement',
          },
          {
            text: footer.lxFooterItemWashingtonConsumerHealthDataPrivacyPolicy(),
            url: '/bumble-washington-consumer-health-data-privacy-policy',
          },
        ].filter(Boolean),
      }}
      socialLinks={[
        {
          url: 'https://www.instagram.com/bumble',
          a11yLabel: `${common.lxA11ySocialLink('Instagram')} ${common.lxA11yOpensInNewWindow}`,
          iconName: Icon.Name.Instagram,
        },
        {
          url: 'https://www.facebook.com/bumbleapp',
          a11yLabel: `${common.lxA11ySocialLink('Facebook')} ${common.lxA11yOpensInNewWindow}`,
          iconName: Icon.Name.Facebook,
        },
        {
          url: 'https://www.twitter.com/bumble',
          a11yLabel: `${common.lxA11ySocialLink('Twitter')} ${common.lxA11yOpensInNewWindow}`,
          iconName: Icon.Name.Twitter,
        },
        {
          url: 'https://www.linkedin.com/company/bumbleinc/',
          a11yLabel: `${common.lxA11ySocialLink('LinkedIn')} ${common.lxA11yOpensInNewWindow}`,
          iconName: Icon.Name.Linkedin,
        },
        {
          url: 'https://www.pinterest.com/bumbleapp',
          a11yLabel: `${common.lxA11ySocialLink('Pinterest')} ${common.lxA11yOpensInNewWindow}`,
          iconName: Icon.Name.Pinterest,
        },
        {
          url: 'https://www.youtube.com/bumble',
          a11yLabel: `${common.lxA11ySocialLink('YouTube')} ${common.lxA11yOpensInNewWindow}`,
          iconName: Icon.Name.Youtube,
        },
        {
          url: 'https://www.tiktok.com/@bumble',
          a11yLabel: `${common.lxA11ySocialLink('TikTok')} ${common.lxA11yOpensInNewWindow}`,
          iconName: Icon.Name.Tiktok,
        },
      ]}
      downloadWidgetText={footer.lxFooterAppDownload()}
      downloadCtaText={footer.lxFooterDownloadTheApp()}
      downloadUrl="https://bumble.onelink.me/3396940749/yuhjm82c"
      languages={languagesList}
      lang={lang}
      languageA11yLabel={common.lxLanguageSelector}
      onLanguageChange={(language: string) => handlePathChange(`/${language}`, language)}
    />
  );
};
